/**
 * <s-card-gallery> — Swipeable photo strip inside a product card.
 *
 * Required markup:
 *   [data-track]      — horizontal scroll-snap track, one full-width slide per photo.
 *   [data-prev]/[data-next] — optional arrow buttons.
 *   [data-dot]        — optional position indicators, one per slide.
 */
class SCardGallery extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.querySelector('[data-prev]')?.addEventListener('click', (e) => this.#go(e, -1), opts);
    this.querySelector('[data-next]')?.addEventListener('click', (e) => this.#go(e, 1), opts);
    this.#track?.addEventListener('scroll', () => this.#update(), { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #track() {
    return this.querySelector('[data-track]');
  }

  get #count() {
    return this.#track?.children.length || 0;
  }

  get #index() {
    const track = this.#track;
    if (!track || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #go(event, step) {
    event.preventDefault();
    event.stopPropagation();
    const track = this.#track;
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const count = this.#count;
    const next = (this.#index + step + count) % count;
    const offset = next * track.clientWidth;
    track.scrollTo({ left: rtl ? -offset : offset });
  }

  #update() {
    const index = this.#index;
    this.querySelectorAll('[data-dot]').forEach((dot, i) => {
      dot.toggleAttribute('data-active', i === index);
    });
  }
}

if (!customElements.get('s-card-gallery')) customElements.define('s-card-gallery', SCardGallery);
